// src/lib/parcoursInvite.ts
//
// Token d'invitation parcours découverte (ATS → candidat).
//
// L'ATS envoie au candidat un lien parcours personnalisé :
//   https://recruteurs.io/decouvrir/<slug>?invite=<uuid>
// Le token est capturé à l'arrivée (landing ou étape) et stocké en
// sessionStorage pour survivre à la navigation entre étapes. Il sert à :
//   - pré-remplir le formulaire final "À vous de jouer"
//   - relier la réponse au bon candidat + mission + client côté ATS

const ATS_URL =
  (import.meta.env.VITE_ATS_API_URL as string | undefined) ??
  'https://app.recruteurs.io';

const storageKey = (slug: string) => `parcours_invite_${slug}`;
const UUID_RE = /^[0-9a-f-]{36}$/i;

/** À appeler au mount des pages parcours : capture ?invite= vers sessionStorage. */
export function captureInviteFromUrl(slug: string): void {
  try {
    const invite = new URLSearchParams(window.location.search).get('invite');
    if (invite && UUID_RE.test(invite)) {
      sessionStorage.setItem(storageKey(slug), invite);
    }
  } catch {
    // sessionStorage indisponible (navigation privée stricte) → tant pis,
    // le formulaire fonctionnera en mode non pré-rempli.
  }
}

export function getInvite(slug: string): string | null {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('invite');
    if (fromUrl && UUID_RE.test(fromUrl)) return fromUrl;
    return sessionStorage.getItem(storageKey(slug));
  } catch {
    return null;
  }
}

export interface InvitePrefill {
  already_responded: boolean;
  candidat: { prenom: string; nom: string; email: string; linkedin_url: string };
  mission: { intitule_poste: string };
}

export async function fetchInvitePrefill(invite: string): Promise<InvitePrefill | null> {
  try {
    const res = await fetch(`${ATS_URL}/api/parcours-invites/${invite}`);
    if (!res.ok) return null;
    const json = await res.json();
    return json?.ok ? (json as InvitePrefill) : null;
  } catch {
    return null;
  }
}

export interface InviteResponsePayload {
  souhaite_rencontrer: boolean;
  message: string;
  contact: { prenom: string; nom: string; email: string; linkedin_url: string };
  /** CV uploadé (signed URL retournée par /api/public-cv-upload). */
  cv_url?: string;
  /**
   * Mode test admin : rediriger l'email client vers cette adresse.
   * Côté ATS, honoré uniquement si l'adresse est membre de l'organisation.
   */
  client_email_override?: string;
}

/** Upload d'un CV (PDF/DOC/DOCX, 10 MB max) → signed URL, ou null si échec. */
export async function uploadCv(file: File): Promise<string | null> {
  try {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${ATS_URL}/api/public-cv-upload`, {
      method: 'POST',
      body: fd,
    });
    if (!res.ok) return null;
    const json = await res.json();
    return json?.url ?? null;
  } catch {
    return null;
  }
}

export async function submitInviteResponse(
  invite: string,
  payload: InviteResponsePayload
): Promise<boolean> {
  try {
    const res = await fetch(`${ATS_URL}/api/parcours-invites/${invite}/response`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return res.ok;
  } catch {
    return false;
  }
}

/** Sans token (lien parcours partagé hors ATS) : notifie le cabinet seulement. */
export async function submitOrphanResponse(
  parcoursSlug: string,
  payload: InviteResponsePayload
): Promise<boolean> {
  try {
    const res = await fetch(`${ATS_URL}/api/parcours-invites/orphan-response`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ parcours_slug: parcoursSlug, ...payload }),
    });
    return res.ok;
  } catch {
    return false;
  }
}
